import { LoadingMojo } from "@web-monorepo/molecules";
import { DDSBodyText, DDSDisplayText } from "@web-monorepo/dds";
import Author from "./Author";
import styles from "./Board.module.css";
import Follow from "./Follow";
import Note from "./Note";
import { useBoard, type UseBoardProps, type UseBoardResult } from "./useBoard.vm";
import Discuss from "src/pods/ideas/components/cards/Discuss";
import DiscussWrapper from "src/pods/ideas/components/cards/DiscussWrapper";
import Watch from "src/pods/ideas/components/cards/Watch";
import getDiscussTopics, { DicussTopic } from "src/pods/ideas/data/getDiscussTopics";
import getWatch from "src/pods/ideas/data/getWatch";
import { Board as BoardType, Lesson, Slide } from "src/pods/ideas/data/types";

const MINDFUL_MOVEMENTS_LESSON_ID = "591a31b0e3fc2622b28b6ad6";

type BoardProps = {
  board: BoardType;
  displayDiscussCard?: (discuss: DicussTopic) => void;
  displayVideoCard?: (watch: Slide, lesson: Lesson, board: BoardType) => void;
  /**
   * DI hook for storybook stories — production callers leave undefined. The
   * default VM async-loads `data/lessons.json`; stories supply canned lessons
   * synchronously to drive both the loading and loaded states.
   */
  di?: {
    useVm?: (props: UseBoardProps) => UseBoardResult;
  };
};

export default function Board({ board, displayDiscussCard, displayVideoCard, di }: BoardProps) {
  const useVm = di?.useVm ?? useBoard;
  const { lessons } = useVm({ board });

  if (!board || !lessons.filter(Boolean).length) {
    return <LoadingMojo />;
  }

  const heroImageUrl = board.image.replace(".png", "@3x.png");

  return (
    <div className={styles.board}>
      <div>
        <div className={styles.header}>
          {/* Decorative hero — the board title below carries the accessible identity, so alt="" keeps the image out of the AT tree. Real <img> avoids a runtime-dynamic `style={{ backgroundImage }}`. */}
          <img className={styles.heroes} src={heroImageUrl} alt="" />
          <DDSDisplayText as="h1" level="md" className={styles.title}>
            {board.title}
          </DDSDisplayText>
          <DDSBodyText level="md" variant="tertiary" className={styles.description}>
            {board.description}
          </DDSBodyText>
          <Note>
            <Author name={board.author.name} photo={board.author.photo} />
            <Follow user={null} following={[]} boardSlug={board.slug} />
          </Note>
        </div>
        <div>
          {lessons.map((lesson) => {
            const videos = getWatch(lesson);
            const discuss = getDiscussTopics(lesson);
            const unbundledComponents = [];

            if (lesson && lesson.release_date && new Date(lesson.release_date) > new Date()) {
              return null;
            }

            if (Array.isArray(videos)) {
              if (lesson._id === MINDFUL_MOVEMENTS_LESSON_ID) {
                videos.forEach((watch, index) => {
                  unbundledComponents.push(
                    <Watch
                      key={`${lesson._id}-watch-${index}`}
                      watch={watch}
                      timing={getTimingOverride(lesson._id)}
                      lesson={{
                        ...lesson,
                        title: getTitleOverride(lesson._id, index),
                      }}
                      onClick={() => displayVideoCard?.(watch, lesson, board)}
                    />,
                  );
                });
              } else {
                videos.forEach((watch) => {
                  unbundledComponents.push(
                    <Watch
                      key={`${lesson._id}-watch`}
                      watch={watch}
                      lesson={lesson}
                      onClick={() => displayVideoCard?.(watch, lesson, board)}
                    />,
                  );
                });
              }
            }

            unbundledComponents.push(
              <DiscussWrapper key={`${lesson._id}-discuss`}>
                {discuss.map((question) => (
                  <Discuss key={question.text} topic={question.text!} onClick={() => displayDiscussCard?.(question)} />
                ))}
              </DiscussWrapper>,
            );

            return unbundledComponents;
          })}
        </div>
      </div>
    </div>
  );
}

function getTitleOverride(lessonId?: string, forIndex?: number) {
  if (lessonId === MINDFUL_MOVEMENTS_LESSON_ID) {
    switch (forIndex) {
      case 0:
        return "Mindful movements: Elephant";
      case 1:
        return "Mindful movements: Triangle";
      case 2:
        return "Mindful movements: Tree";
      default:
        throw new Error("unexpected case in getTitleOverride");
    }
  }
}

function getTimingOverride(lessonId?: string) {
  if (lessonId === MINDFUL_MOVEMENTS_LESSON_ID) {
    return "1 minute video";
  }
}
